import React, { useState, useEffect } from 'react';
import { Center, Spinner, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Button, Text, SimpleGrid, VStack, HStack, Box } from '@chakra-ui/react';
import '../styles/components/AppointmentBookingModal.css';

const AppointmentBookingModal = ({ isOpen, onClose, _pos, provider, appointment, onConfirmBooking, isBooking, bookingError, selectedAppointment, isPlanned }) => {
    const [selectedTime, setSelectedTime] = useState(null);
    const [isConfirmMode, setIsConfirmMode] = useState(false);
    const [rescheduling, setRescheduling] = useState(false);

    useEffect(() => {
        if (isBooking) {
            setRescheduling(!!selectedAppointment && !isPlanned);
        }
    }, [isBooking, selectedAppointment, isPlanned]);

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
    };

    const handleBook = () => {
        setIsConfirmMode(true);
    };

    const handleConfirm = async () => {
        await onConfirmBooking(_pos, appointment.date, selectedTime);
        setRescheduling(false);
        onClose();
    };

    const handleCancelConfirm = () => {
        setIsConfirmMode(false);
        setSelectedTime(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!appointment || !appointment.date) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay className="modal-overlay" />
            <ModalContent className="modal-content">
                <ModalHeader className="modal-header">
                    {rescheduling || isBooking
                        ? "Please wait..."
                        : isConfirmMode
                            ? (selectedAppointment && !isPlanned ? "Confirm Reschedule" : "Confirm Booking")
                            : provider.FullName}
                </ModalHeader>
                <ModalCloseButton className="close-button" isDisabled={rescheduling || isBooking} />
                <ModalBody className="modal-body">
                    {(rescheduling || isBooking) ? (
                        <Center className="loading-center">
                            <Spinner className="loading-spinner" size="lg" />
                            <Text ml={4} className="loading-text">
                                {rescheduling ? 'Rescheduling appointment...' : 'Booking appointment...'}
                            </Text>
                        </Center>
                    ) : isConfirmMode ? (
                        <VStack spacing={5} align="stretch">
                            {!isPlanned && selectedAppointment && (
                                <>
                                    <Text className="section-title">Current Appointment:</Text>
                                    <Box className="appointment-box current-appointment">
                                        <Text><strong>Provider:</strong> {selectedAppointment.ProvFullName}</Text>
                                        <Text><strong>Date:</strong> {formatDate(selectedAppointment.AptDateTime)}</Text>
                                        <Text><strong>Time:</strong> {new Date(selectedAppointment.AptDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                                    </Box>
                                </>
                            )}
                            <Text className="section-title">
                                {selectedAppointment && !isPlanned ? 'New Appointment' : 'Appointment Details'}:
                            </Text>
                            <Box className="appointment-box new-appointment">
                                <Text><strong>Provider:</strong> {provider.FullName}</Text>
                                <Text><strong>Date:</strong> {formatDate(appointment.date)}</Text>
                                <Text><strong>Time:</strong> {selectedTime}</Text>
                            </Box>
                            <Text className="confirmation-question">
                                {!isPlanned && selectedAppointment 
                                    ? `Do you want to reschedule your appointment from ${formatDate(selectedAppointment.AptDateTime)} at ${new Date(selectedAppointment.AptDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} with ${selectedAppointment.ProvFullName} to ${formatDate(appointment.date)} at ${selectedTime} with ${provider.FullName}?`
                                    : `Do you want to book this appointment on ${formatDate(appointment.date)} at ${selectedTime} with ${provider.FullName}?`}
                            </Text>
                        </VStack>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            <Box className="provider-info">
                                <Text><strong>Specialty:</strong> {provider.SpecialtyName}</Text>
                                <Text><strong>Date:</strong> {formatDate(appointment.date)}</Text>
                            </Box>

                            <Text className="time-selection-prompt">Select your preferred appointment time:</Text>

                            <SimpleGrid columns={[2, 3, 4, 5]} spacing={3} mt={2}>
                                {appointment.time.map((slot, index) => (
                                    <Button
                                        key={index}
                                        className={`time-slot-button ${selectedTime === slot ? 'selected' : ''}`}
                                        onClick={() => handleTimeSelect(slot)}
                                    >
                                        {slot}
                                    </Button>
                                ))}
                            </SimpleGrid>
                        </VStack>
                    )}
                    {bookingError && (
                        <Box className="error-box">
                            <Text className="error-text">{bookingError}</Text>
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter className="modal-footer">
                    {(rescheduling || isBooking) ? null : isConfirmMode ? (
                        <HStack spacing={3}>
                            <Button 
                                className="modal-button confirm-button"
                                colorScheme="blue" 
                                onClick={handleConfirm}
                            >
                                Yes, Confirm
                            </Button>
                            <Button 
                                className="modal-button cancel-button"
                                onClick={handleCancelConfirm}
                            >
                                No, Go Back
                            </Button>
                        </HStack>
                    ) : (
                        <HStack spacing={3}>
                            <Button 
                                className={`modal-button book-button ${selectedAppointment && !isPlanned ? 'reschedule' : 'book'}`}
                                colorScheme={selectedAppointment && !isPlanned ? "blue" : "green"} 
                                onClick={handleBook} 
                                isDisabled={!selectedTime}
                            >
                                {selectedAppointment && !isPlanned ? 'Reschedule Appointment' : 'Book Appointment'}
                            </Button>
                            <Button 
                                className="modal-button close-button-custom"
                                onClick={onClose}
                            >
                                Close
                            </Button>
                        </HStack>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AppointmentBookingModal;